// THE derived job title — « {type} — {client} » — in ONE place.
//
// Three intake doors compose it (the console capture form, its handheld twin,
// and the évaluation door), and both job pages need to recognise it in order to
// stay quiet about it: printing « Traitement — mineuse du thuya — François
// Gagnon » under a heading that already says the customer, beside a chip that
// already says the type, is the same fact three times.
//
// ⚠ The customer half is the job's `customer_name` — COMPANY FIRST, mirroring
// `Job#customer_name`. It is NOT the site contact's full name: on a B2B job the
// two differ, which is exactly why the old suppression (comparing the title to
// the page's contact-derived headline) never fired.
//
// The separator is an em dash with spaces, U+2014, matching what the creators
// join with.
const SEPARATOR = ' — '

export function derivedJobTitle(templateName?: string | null, customerName?: string | null): string {
  return [ templateName, customerName ].filter(Boolean).join(SEPARATOR)
}

// Is this title just the two facts the page already shows?
//
// Accepted degradation: renaming the job TYPE or the CUSTOMER desynchronises
// the stored title from any recomputation, and the line reappears. That is the
// safe direction — a slightly redundant line costs a glance, whereas the other
// failure mode would HIDE words a human wrote.
//
// The second shape is « {type} — {street} » (the street without its civic
// number), which the demo book writes — the page shows the type AND the
// address right above it, so it is the same restatement.
// The street without its civic number — « 1630 rue de la Concorde » → « rue de
// la Concorde ». What a list writes on a dossier line when the title only
// restated the type and the customer (DossierCell, design 169), and the second
// shape isDerivedJobTitle recognises. Empty when there is no street.
export function streetOf(addressLine?: string | null): string {
  return (addressLine ?? '').trim().replace(/^\d+[a-zA-Z]?\s+/, '')
}

export function isDerivedJobTitle(
  title?: string | null,
  templateName?: string | null,
  customerName?: string | null,
  addressLine?: string | null,
): boolean {
  if (!title) return false
  const trimmed = title.trim()
  const derived = derivedJobTitle(templateName, customerName)
  if (derived.length > 0 && trimmed === derived) return true
  const street = streetOf(addressLine)
  return !!templateName && !!street && trimmed === derivedJobTitle(templateName, street)
}
